import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getChrYearly } from '../../api/chrYearly';
import { peopleCostSynthesis, peopleCostSynthesisYtd, listHrPeople, listPayrollMovements } from '../../api/hr';
import { fetchPersonnelCostCascade } from '../../api/tdabc/personnelCostCascade';
import { getHypotheticalIndirect } from '../../api/tdabc/hypotheticalIndirect';
import { computeCcr } from './ccrModel';
import { formatNumber } from '../../utils/format';
import { p } from '../../i18n/useP';
import StoryDeck from '../../components/StoryDeck';
import './PeopleStory.css';

/* Chiffres clés — la Synthèse racontée (proto validé le 2026-08-21).

   Deux écrans répondaient à la même question sans se parler : la Synthèse
   (« où j'en suis ») et l'Évolution annuelle (« d'où je viens »). Les lire
   l'un après l'autre laissait au lecteur le travail de rapprochement — celui
   qui produit justement l'information utile.

   D'où une seule page, en CHAPITRES qui s'aimantent au défilement : chacun
   tient un écran, pose une phrase, montre un chiffre. Le récit ne CRÉE aucune
   donnée ; il ordonne celles des deux écrans et les rapproche.

   Le défilement aimanté vit dans un conteneur PROPRE à cette page : le rendre
   global changerait le comportement de toutes les autres.

   Chapitre 3 CONDITIONNEL : le paradoxe (masse et taux qui divergent) n'existe
   pas tous les ans ni chez tous les clients. Un chapitre fixe afficherait une
   page creuse — il ne paraît que si l'écart dépasse le seuil. */

const SEUIL_PARADOXE = 5;   // points d'écart entre variation de masse et d'heures
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
              'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MOIS_C = ['jan.', 'fév.', 'mars', 'avr.', 'mai', 'juin',
                'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

const nb = (v, dec = 0) => formatNumber(v, dec);
/* Libellé de figure-clé sur DEUX lignes : le dernier mot passe dessous. Les
   libellés longs (« Masse salariale chargée », « Taux de charges sociales
   employeur ») s'étiraient sur une ligne bien plus large que le chiffre
   qu'ils nomment, et écrasaient la rangée. La coupure se prend sur la chaîne
   DÉJÀ traduite : elle suit donc la langue au lieu de figer deux moitiés que
   l'anglais remettrait dans l'autre ordre. */
const twoLines = (s) => {
  const i = String(s).lastIndexOf(' ');
  return i < 0 ? s : <>{String(s).slice(0, i)}<br />{String(s).slice(i + 1)}</>;
};
const pctVar = (now, was) => (was ? (now / was - 1) * 100 : null);
const signed = (v, dec = 0) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${nb(Math.abs(v), dec)}`;
/* Décimale à la demande : arrondi à l'entier, une variation de −0,5 % s'affiche
   « 0 % » — le lecteur voit un montant qui bouge et un taux qui dit que rien
   n'a bougé. On garde l'entier au-delà de 1 %, où la décimale n'apporte rien. */
const pctDec = (v) => (v != null && Math.abs(v) < 1 && Math.abs(v) > 0 ? 1 : 0);

/* Un chiffre qui entre en comptant : le mouvement dit « ceci est le résultat
   d'un calcul ». Neutralisé si la personne a demandé moins d'animation. */
function Fig({ value, dec = 0, unit, label, sign, tone }) {
  const ref = useRef(null);
  const [shown, setShown] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { setShown(value); return undefined; }
    let raf = 0, t0 = null;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const tick = (ts) => {
        if (t0 === null) t0 = ts;
        const k = Math.min(1, (ts - t0) / 900);
        setShown(value * (1 - Math.pow(1 - k, 3)));
        if (k < 1) raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    io.observe(el);
    return () => { io.disconnect(); cancelAnimationFrame(raf); };
  }, [value]);
  return (
    <div className={`pst-fig${tone ? ` pst-fig--${tone}` : ''}`} ref={ref}>
      <div className="pst-fig__v">
        {sign && shown !== 0 ? (shown > 0 ? '+' : '−') : ''}{nb(Math.abs(shown), dec)}
        {unit && <small>{unit}</small>}
      </div>
      <div className="pst-fig__k">{label}</div>
    </div>
  );
}

/* Passerelle vers « Chiffres clés » : le récit donne la lecture, la section
   voisine donne le détail chiffré. Sans ce lien, le lecteur convaincu par la
   première phrase devait retourner au menu pour aller voir les nombres. */
function ChiffresCta({ onGo }) {
  return (
    <button type="button" className="pst-cta" onClick={onGo}>
      {p('Voir les chiffres clés')}
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 12h13" /><path d="m12 5 7 7-7 7" />
      </svg>
    </button>
  );
}

export default function PeopleStory({ activeTenant, year, onGo,
                                      print = false, onDataReady = null }) {
  const tenantId = activeTenant?.id;
  const [now, setNow] = useState(null);
  const [was, setWas] = useState(null);
  /* Deux moteurs, deux mesures — assumées et NOMMÉES différemment :
       · people-synthesis  → COÛT DU PERSONNEL (rémunération + charges + frais),
         seul à savoir s'arrêter au dernier mois clos (variante ytd) ;
       · chr yearly        → heures disponibles et SALAIRE HORAIRE, base masse
         chargée (sans frais), identique à la section Salaire horaire.
     Les mélanger dans un même chiffre produirait un écart inexplicable ; le
     récit les garde donc dans des chapitres séparés. */
  const [ytd, setYtd] = useState(null);
  const [full, setFull] = useState(null);
  /* TROISIÈME moteur, assumé comme les deux autres : la cascade TD-ABC, seule
     à connaître la capacité pratique et les charges indirectes, donc seule à
     savoir dire ce que coûte une heure. Sa formule vit dans `ccrModel`,
     partagée avec le récit du coût horaire : deux copies donneraient deux CCR
     qui divergent, sur deux écrans lus à cinq minutes d'intervalle. */
  const [ccrNow, setCcrNow] = useState(null);
  const [ccrWas, setCcrWas] = useState(null);
  const [prev, setPrev] = useState(null);
  const [people, setPeople] = useState([]);
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(false);
  /* Contrat du registre d'impression : prévenir quand les données sont là, pour
     que le PDF ne parte pas sur un récit à moitié rempli. */
  const readySent = useRef(false);
  useEffect(() => {
    if (!onDataReady || loading || readySent.current) return;
    readySent.current = true;
    onDataReady();
  }, [loading, onDataReady]);
  const deckRef = useRef(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!tenantId || !year) { setNow(null); setWas(null); return; }
    let alive = true;
    setLoading(true);
    const ok = (r) => (r && !r.error ? (r.data ?? r) : null);
    Promise.all([
      getChrYearly(tenantId, year).catch(() => ({ error: true })),
      getChrYearly(tenantId, year - 1).catch(() => ({ error: true })),
      peopleCostSynthesisYtd(tenantId, year, 'all').catch(() => ({ error: true })),
      peopleCostSynthesis(tenantId, year, 'all').catch(() => ({ error: true })),
      peopleCostSynthesis(tenantId, year - 1, 'all').catch(() => ({ error: true })),
      listHrPeople(tenantId).catch(() => ({ error: true })),
      listPayrollMovements(tenantId).catch(() => ({ error: true })),
      fetchPersonnelCostCascade(tenantId, year).catch(() => null),
      fetchPersonnelCostCascade(tenantId, year - 1).catch(() => null),
      getHypotheticalIndirect(tenantId, year).catch(() => ({ error: true })),
      getHypotheticalIndirect(tenantId, year - 1).catch(() => ({ error: true })),
    ]).then(([a, b, y, f, pv, pp, mv, cn, cw, bn, bw]) => {
      if (!alive) return;
      setNow(ok(a)); setWas(ok(b));
      setYtd(ok(y)); setFull(ok(f)); setPrev(ok(pv));
      setPeople(ok(pp) || []); setMoves(ok(mv) || []);
      /* Base de charges indirectes NON saisie = 0, comme sur l'écran du coût
         horaire : le récit doit montrer le même taux que lui, y compris quand
         il est incomplet. Inventer une base ici les ferait diverger. */
      const amt = (r) => (r && !r.error ? Number(r.data?.amount ?? r.data ?? 0) : 0);
      setCcrNow(cn ? computeCcr(cn.totals, amt(bn)) : null);
      setCcrWas(cw ? computeCcr(cw.totals, amt(bw)) : null);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [tenantId, year]);

  const sumCost = (r) => (r?.rows || []).reduce((t, x) => t + (Number(x.total_cost_empr) || 0), 0);
  const sumPart = (r, k) => (r?.rows || []).reduce((t, x) => t + (Number(x[k]) || 0), 0);

  const story = useMemo(() => {
    if (!now?.kpis) return null;
    const k = now.kpis, t = now.totals || {};
    const kw = was?.kpis, tw = was?.totals || {};
    const masse = t.loaded_payroll || 0;
    const heures = k.total_heures_contractuelles || 0;
    const masseW = kw ? (tw.loaded_payroll || 0) : null;
    const heuresW = kw ? (kw.total_heures_contractuelles || 0) : null;
    const dMasse = masseW ? pctVar(masse, masseW) : null;
    const dHeures = heuresW ? pctVar(heures, heuresW) : null;
    const dLcr = kw ? pctVar(k.lcr_moyen, kw.lcr_moyen) : null;
    /* À DATE : le récit s'ouvre là où l'entreprise en est vraiment. Un client
       clos à juillet ne veut pas lire son année entière en première phrase —
       il veut savoir ce qu'il a déjà dépensé. Sans clôture intermédiaire, le
       chapitre bascule sur l'année pleine. */
    const cA = ytd ? sumCost(ytd.synthesis) : null;
    const cN = full ? sumCost(full) : null;
    const cP = prev ? sumCost(prev) : null;
    const through = ytd?.through_month || null;

    return {
      year: now.year, k, t, kw, tw, masse, heures, masseW, heuresW, dMasse, dHeures, dLcr,
      coutADate: cA, coutAnnee: cN, coutPrev: cP,
      through, periodLabel: ytd?.period_label || null,
      capacity: ytd?.capacity || null, turnover: ytd?.turnover || null,
      resteAVenir: (cA != null && cN != null) ? Math.max(0, cN - cA) : null,
      dCout: (cN != null && cP) ? pctVar(cN, cP) : null,
      remun: full ? sumPart(full, 'effective_remuneration') : null,
      social: full ? sumPart(full, 'social_charges_empr') : null,
      fees: full ? sumPart(full, 'other_fees') : null,
      // Décomposition À DATE : la masse chargée (rémunérations + charges
      // sociales employeur) d'un côté, les frais de personnel de l'autre.
      // Les confondre dans un seul « coût » masque que l'un se négocie et
      // l'autre se subit.
      remunA:  ytd ? sumPart(ytd.synthesis, 'effective_remuneration') : null,
      socialA: ytd ? sumPart(ytd.synthesis, 'social_charges_empr') : null,
      feesA:   ytd ? sumPart(ytd.synthesis, 'other_fees') : null,
      // La ligne de synthèse ne porte que le TAUX LPP employeur, pas le
      // montant : on le reconstitue sur la rémunération effective.
      lppA: ytd ? (ytd.synthesis.rows || []).reduce(
        (t, r) => t + (Number(r.effective_remuneration) || 0) * (Number(r.lpp_empr_pct) || 0) / 100, 0) : null,
      // Le paradoxe : la masse et le taux horaire partent dans des sens opposés
      // parce que les heures bougent plus vite que la masse.
      paradoxe: dMasse != null && dHeures != null && dLcr != null
        && Math.abs(dHeures - dMasse) >= SEUIL_PARADOXE
        && Math.sign(dLcr) !== Math.sign(dMasse),
    };
  }, [now, was, ytd, full, prev]);

  /* CLAVIER — flèches haut/bas pour passer d'un chapitre à l'autre.
     Le défilement aimanté seul obligeait à viser à la souris ; une
     présentation se parcourt au clavier.

     L'écoute est posée sur la FENÊTRE, parce que le lecteur n'a aucune raison
     d'avoir cliqué dans le cadre avant d'appuyer sur une flèche. Elle
     s'efface dès que le focus est dans un champ de saisie — sinon on volerait
     les flèches à un formulaire ouvert ailleurs sur la page. */
  useEffect(() => {
    const deck = deckRef.current;
    if (!deck) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const el = document.activeElement;
      const tag = el && el.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (el && el.isContentEditable)) return;
      const slides = Array.from(deck.querySelectorAll('.pst-slide'));
      if (!slides.length) return;
      /* Le chapitre courant se lit sur la POSITION DE DÉFILEMENT, pas sur un
         état React : au premier affichage, aucun chapitre n'a encore été
         « observé » comme actif, et se fier à cet état laissait la première
         flèche sans effet — c'est le seul endroit où l'on n'avait rien
         défilé. */
      let cur = 0;
      slides.forEach((sl, i) => { if (sl.offsetTop <= deck.scrollTop + 4) cur = i; });
      let to = null;
      if (e.key === 'ArrowDown' || e.key === 'PageDown') to = Math.min(slides.length - 1, cur + 1);
      else if (e.key === 'ArrowUp' || e.key === 'PageUp') to = Math.max(0, cur - 1);
      else if (e.key === 'Home') to = 0;
      else if (e.key === 'End') to = slides.length - 1;
      if (to === null || to === cur) return;
      e.preventDefault();
      deck.scrollTo({ top: slides[to].offsetTop, behavior: 'smooth' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    /* Dépendance sur `story` et non sur le chapitre actif : tant que les
       données ne sont pas là, le cadre n'est pas monté et l'écouteur ne peut
       pas s'attacher. Avec l'ancienne dépendance, il ne s'attachait qu'au
       premier changement de chapitre — donc jamais depuis la première slide. */
  }, [story]);

  // Rail de progression : le chapitre le plus visible s'allume.
  useEffect(() => {
    const deck = deckRef.current;
    if (!deck || !story) return undefined;
    const slides = Array.from(deck.querySelectorAll('.pst-slide'));
    /* Sans IntersectionObserver, seul le SURLIGNAGE du rail se perd — la
       lecture, elle, marche. Le récit entier ne doit pas tomber pour un
       repère de navigation. */
    if (!window.IntersectionObserver) return undefined;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.5) setActive(slides.indexOf(e.target));
      });
    }, { root: deck, threshold: [0, 0.5, 1] });
    slides.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, [story]);

  if (loading) return <div className="hr-page__loading">{p('Chargement…')}</div>;
  if (!story) return <div className="hr-page__empty">{p('Aucune donnée de personnel sur cet exercice.')}</div>;

  const { k, t, story: _s } = story;   // eslint-disable-line
  const kchf = (v) => nb(v / 1000, 0);
  const goSlide = (i) => {
    const deck = deckRef.current;
    const s = deck && deck.querySelectorAll('.pst-slide')[i];
    if (s) deck.scrollTo({ top: s.offsetTop, behavior: 'smooth' });
  };

  /* Entrées et sorties de l'année, NOMMÉES, et leur poids sur la masse.
     Le coût vient de la synthèse à date : c'est ce que la personne a coûté
     sur les mois clos, pas un salaire annuel théorique. */
  const rowsA = ytd?.synthesis?.rows || [];
  const massOf = (pid) => {
    const r = rowsA.find((x) => x.person_id === pid);
    return r ? (Number(r.effective_remuneration) || 0) + (Number(r.social_charges_empr) || 0) : 0;
  };
  const inWindow = (d) => {
    if (!d) return null;
    const [yy, mm] = String(d).split('-').map(Number);
    return (yy === story.year && (!story.through || mm <= story.through)) ? mm : null;
  };
  const movIn = [], movOut = [];
  let massIn = 0, massOut = 0;
  (people || []).forEach((x) => {
    const label = `${x.first_name || ''} ${x.last_name || ''}`.trim() || '—';
    const mi = inWindow(x.entry_date);
    if (mi) { movIn.push({ id: `i${x.id}`, name: label, m: mi }); massIn += massOf(x.id); }
    const mo = inWindow(x.termination_date);
    if (mo) { movOut.push({ id: `o${x.id}`, name: label, m: mo }); massOut += massOf(x.id); }
  });
  movIn.sort((a, b) => a.m - b.m); movOut.sort((a, b) => a.m - b.m);

  /* Mouvements SIMULÉS portant sur les mois ouverts, regroupés PAR CAUSE et
     non par personne : à l'échelle de l'atterrissage, ce qui compte est ce
     qui fait bouger la masse — pas qui. Une liste nominative de douze lignes
     obligeait à faire soi-même le total par nature.

     Les causes reprennent EXACTEMENT le découpage logique de Planification
     (LOGICAL dans HrPlanningPage) : un `departure` AVEC personne est une fin
     de contrat, un `departure` SANS personne est une réduction générale — un
     poste qu'on supprime sans savoir encore lequel. Les confondre reviendrait
     à annoncer un départ nominatif là où il n'y a qu'une décision d'effectif.

     Les causes vides ne s'affichent pas. */
  const CAUSES = [
    { id: 'hire',    label: p('Embauches prévues'),         tone: 'in'  },
    { id: 'end',     label: p('Fins de contrat'),           tone: 'out' },
    { id: 'cut',     label: p('Réductions générales'),      tone: 'out' },
    { id: 'rate',    label: p('Changements de taux'),       tone: 'chg' },
    { id: 'salary',  label: p('Modifications du salaire'),  tone: 'chg' },
  ];
  const causeOf = (m) => {
    if (m.movement_type === 'hire') return 'hire';
    if (m.movement_type === 'departure') return m.person_id ? 'end' : 'cut';
    if (m.movement_type === 'rate_change' || m.movement_type === 'fte_delta') return 'rate';
    return 'salary';                       // salary_change · bonus
  };

  /* Tout est exprimé en SALAIRE CHARGÉ, une seule unité :
       · changement de taux / départ nominatif → on connaît la personne, donc
         son salaire chargé mensuel réel (ligne CHR ÷ mois actifs). Un taux qui
         passe de 100 % à 0 % pendant cinq mois est une ÉCONOMIE de cinq mois
         de salaire chargé — pas un mouvement « sans montant ».
       · embauche, modification de salaire, réduction générale → le montant
         saisi est un BRUT mensuel, majoré du taux de charges employeur moyen
         de l'exercice pour rester dans la même unité.
     Le signe suit l'effet sur la masse : négatif = économie. */
  const chrRows = new Map((now.rows || []).map((r) => [r.person_id, r]));
  const emprRate = Number(t.gross_salary) > 0
    ? Number(t.social_charges_empr) / Number(t.gross_salary) : 0;
  const loadedMonthOf = (pid) => {
    const r = chrRows.get(pid);
    if (!r) return null;
    const mm = Number(r.months_in_year) || 12;
    return mm > 0 ? (Number(r.loaded_payroll) || 0) / mm : null;
  };

  const byCause = {};
  let planImpact = 0;
  let planPartial = false;
  (moves || []).forEach((m) => {
    const sm = m.start_month ? String(m.start_month).slice(0, 7).split('-').map(Number) : null;
    if (!sm || sm[0] !== story.year) return;
    if (story.through && sm[1] <= story.through) return;      // déjà passé
    const em = m.end_month ? String(m.end_month).slice(0, 7).split('-').map(Number) : null;
    const lastM = em && em[0] === story.year ? em[1] : 12;
    const months = Math.max(0, lastM - sm[1] + 1);
    const c = causeOf(m);

    let effect = null;                       // CHF chargés sur la période
    if (c === 'rate') {
      const r = chrRows.get(m.person_id);
      const mon = loadedMonthOf(m.person_id);
      const cur = r ? (Number(r.fte_pct) || 0) / 100 : 0;
      if (mon != null && cur > 0) {
        const next = Number(m.fte) || 0;
        effect = -mon * (1 - next / cur) * months;      // < 0 si le taux baisse
      }
    } else if (c === 'end') {
      const mon = loadedMonthOf(m.person_id);
      if (mon != null) effect = -mon * months;
    }
    if (effect === null) {
      const amt = Number(m.monthly_amount) || 0;
      if (amt !== 0) {
        const signed = (c === 'cut' || c === 'end') ? -Math.abs(amt) : amt;
        effect = signed * (1 + emprRate) * months;      // brut saisi → chargé
      }
    }
    if (effect === null) { planPartial = true; effect = 0; }
    else planImpact += effect;

    const b = byCause[c] || (byCause[c] = { n: 0, amount: 0, chf: false, from: 13, to: 0 });
    b.n += 1;
    b.amount += effect;
    if (effect !== 0) b.chf = true;
    b.from = Math.min(b.from, sm[1]);
    b.to = Math.max(b.to, lastM);
  });
  const plannedCauses = CAUSES
    .filter((c) => byCause[c.id])
    .map((c) => ({ ...c, ...byCause[c.id] }));

  const chapters = [];

  /* (Le chapitre « Par où commencer », trois portes en tête de récit, a été
     RETIRÉ le 2026-09-01 : la section Accueil pose désormais ces choix en
     pleine page, avant même d'ouvrir le récit.) */
  const aDate = story.through && story.coutADate != null;
  chapters.push(
    { key: 'now', label: p('Où j’en suis'), node: (<>
      {aDate ? (
        <>
          <h2>
            {p('À fin')} {MOIS[story.through - 1]}, {p('les contrats et frais représentent')} {kchf(story.coutADate)} {p('milliers de francs.')}
          </h2>
          <p className="pst-lede">
            {story.periodLabel} — {p('sur la base des contrats et des taux de charges sociales employeur.')}{' '}
            {p('Les indemnités perçues — assurance perte de gain, RHT — n’en sont pas déduites.')}
          </p>
          <div className="pst-figs">
            <Fig value={(story.remunA + story.socialA) / 1000} unit="kCHF" label={twoLines(p('Masse salariale chargée'))} />
            <Fig value={story.feesA / 1000} unit="kCHF" label={p('Frais de personnel')} />
            <Fig value={story.remunA ? (story.socialA / story.remunA) * 100 : 0} dec={1} unit="%"
                 label={twoLines(p('Taux de charges sociales employeur'))} />
          </div>
          {story.lppA > 0 && story.remunA > 0 && (
            <p className="pst-foot">
              {p('dont LPP')} {nb((story.lppA / story.remunA) * 100, 1)} % {p('des rémunérations')} — {kchf(story.lppA)} kCHF
            </p>
          )}
          {onGo && <ChiffresCta onGo={onGo} />}
        </>
      ) : (
        <>
          <h2>{p('Les contrats et frais représentent')} {kchf(story.coutAnnee ?? story.masse)} {p('milliers de francs cette année.')}</h2>
          <p className="pst-lede">
            {p('Aucune clôture intermédiaire sur cet exercice : la lecture porte sur l’année entière, sur la base des contrats et des taux de charges sociales employeur.')}{' '}
            {p('Les indemnités perçues — assurance perte de gain, RHT — n’en sont pas déduites.')}
          </p>
          <div className="pst-figs">
            <Fig value={(story.coutAnnee ?? story.masse) / 1000} unit="kCHF" label={p('Coût du personnel')} />
            <Fig value={k.nb_personnes} label={p('Collaborateurs')} />
            <Fig value={t.fte || 0} dec={1} unit="EPT" label={p('Équivalents plein temps')} />
          </div>
          {onGo && <ChiffresCta onGo={onGo} />}
        </>
      )}
    </>) },
  );

  /* ── Chapitre 2 : ce qui a changé — les mouvements RÉELS ─────────────
     Les compteurs d'entrées et de sorties ne disent rien tant qu'on ne sait
     pas QUI : un départ de senior et une embauche de stagiaire font « 1 et
     1 » et n'ont rien à voir. D'où les noms, et l'effet chiffré sur la masse. */
  if (aDate && (movIn.length > 0 || movOut.length > 0)) {
    const net = massIn - massOut;
    chapters.push(
      { key: 'moves', label: p('Ce qui a changé'), tone: 'tint', node: (<>
        <h2>
          {movIn.length} {movIn.length > 1 ? p('arrivées') : p('arrivée')} {p('et')} {movOut.length}{' '}
          {movOut.length > 1 ? p('départs') : p('départ')} {p('depuis janvier.')}
        </h2>
        <div className="pst-moves">
          <div className="pst-moves__col">
            <b className="pst-moves__h pst-moves__h--in">{p('Arrivées')}</b>
            {movIn.length === 0 && <span className="pst-moves__none">—</span>}
            {movIn.map((x) => (
              <span className="pst-moves__row" key={x.id}>
                <span className="pst-moves__name">{x.name}</span>
                <span className="pst-moves__when">{MOIS_C[x.m - 1]}</span>
              </span>
            ))}
          </div>
          <div className="pst-moves__col">
            <b className="pst-moves__h pst-moves__h--out">{p('Départs')}</b>
            {movOut.length === 0 && <span className="pst-moves__none">—</span>}
            {movOut.map((x) => (
              <span className="pst-moves__row" key={x.id}>
                <span className="pst-moves__name">{x.name}</span>
                <span className="pst-moves__when">{MOIS_C[x.m - 1]}</span>
              </span>
            ))}
          </div>
        </div>
        {/* Deux indicateurs, pas quatre : la masse apportée et la masse partie
            se lisent déjà dans les deux colonnes de noms au-dessus. Ne reste
            que ce qu'on ne peut pas déduire — le taux et le solde. */}
        <div className="pst-figs pst-figs--tight">
          {story.turnover && (
            <Fig value={story.turnover.rate_annualized_pct} dec={1} unit="%"
                 label={p('Rotation annualisée *')} />
          )}
          <Fig value={net / 1000} unit="kCHF" sign label={p('Effet sur la masse chargée')} />
        </div>
        {/* La ligne s'ouvrait sur sa formule sans dire de QUOI elle est la
            formule, et séparait les mots de leur application par un tiret. Le
            nom du taux passe devant, et la chaîne d'égalités remplace le
            tiret : rotation = la règle = les nombres = le résultat. */}
        {story.turnover && (
          <p className="pst-foot pst-foot--formula">
            * {p('rotation')} = {p('sorties ÷ effectif moyen × 12 ÷ mois clos')} = {story.turnover.exits} ÷{' '}
            {nb(story.turnover.avg_headcount, 1)} × 12 ÷ {story.through} = {nb(story.turnover.rate_annualized_pct, 1)} %
          </p>
        )}
      </>) },
    );
  }

  /* ── Chapitre 3 : l'atterrissage ─────────────────────────────────────
     Il n'a de sens qu'en cours d'année : sur un exercice clos, « où je vais
     finir » et « où j'en suis » sont le même chiffre. */
  if (aDate && story.coutAnnee != null && story.resteAVenir != null) {
    chapters.push(
      { key: 'land', label: p('Où je vais atterrir'), node: (<>
        <h2>{p('L’année devrait atterrir à')} {kchf(story.coutAnnee + planImpact)} {p('milliers de francs.')}</h2>
        <p className="pst-lede">
          {p('Les contrats en vigueur portent le socle ; les mouvements simulés s’y ajoutent.')}
        </p>

        {/* Une seule barre, deux parts d'un même tout : le déjà engagé et le
            reste. Deux barres séparées ne se comparaient pas — elles
            laissaient croire à deux grandeurs indépendantes. */}
        <div className="pst-split">
          <div className="pst-split__bar">
            <span className="pst-split__seg" style={{ width: `${(story.coutADate / story.coutAnnee) * 100}%` }}>
              {kchf(story.coutADate)}
            </span>
            <span className="pst-split__seg pst-split__seg--rest"
                  style={{ width: `${(story.resteAVenir / story.coutAnnee) * 100}%` }}>
              {kchf(story.resteAVenir)}
            </span>
          </div>
          <div className="pst-split__legend">
            <span><i /> {p('engagé sur')} {story.through} {p('mois clos')}</span>
            <span><i className="rest" /> {p('reste à engager sur')} {12 - story.through} {p('mois')}</span>
          </div>
        </div>

        {plannedCauses.length > 0 && (
          <div className="pst-plan">
            <b className="pst-plan__h">{p('Mouvements prévus, par cause')} <small>{p('· effet en salaire chargé')}</small></b>
            {plannedCauses.map((c) => (
              <span className="pst-plan__row" key={c.id}>
                <span className={`pst-plan__tag pst-plan__tag--${c.tone}`}>{c.n}</span>
                <span className="pst-plan__lbl">{c.label}</span>
                <span className="pst-plan__when">
                  {c.from === c.to ? MOIS_C[c.from - 1] : `${MOIS_C[c.from - 1]} → ${MOIS_C[c.to - 1]}`}
                </span>
                <span className="pst-plan__amt">
                  {c.chf ? `${signed(c.amount)} CHF` : '—'}
                </span>
              </span>
            ))}
          </div>
        )}

        {/* Le socle vient des CONTRATS seuls (la synthèse ne connaît pas les
            mouvements) ; l'effet des mouvements s'y ajoute. Afficher le socle
            comme « atterrissage » laissait croire que les mouvements étaient
            déjà dedans. */}
        <div className="pst-figs pst-figs--tight">
          <Fig value={story.coutAnnee / 1000} unit="kCHF" label={p('Socle des contrats')} />
          {planImpact !== 0 && (
            <Fig value={planImpact / 1000} unit="kCHF" sign label={p('Effet des mouvements')} />
          )}
          <Fig value={(story.coutAnnee + planImpact) / 1000} unit="kCHF"
               label={p('Atterrissage du coût total')} />
        </div>
        {planPartial && (
          <p className="pst-foot">
            {p('Certains mouvements n’ont ni montant saisi ni personne rattachée : leur effet ne peut pas être chiffré et n’est pas compté ci-dessus.')}
          </p>
        )}
      </>) },
    );
  }

  if (story.coutPrev && story.coutAnnee != null && story.dCout != null) {
    const up = story.dCout >= 0;
    const maxC = Math.max(story.coutAnnee, story.coutPrev);
    chapters.push(
      { key: 'was', label: p('D’où je viens'), node: (<>
        <h2>
          {p('En un an, le coût du personnel a')} {up ? p('progressé de') : p('reculé de')}{' '}
          {nb(Math.abs(story.dCout), pctDec(story.dCout))} %.
        </h2>
        <p className="pst-lede">
          {p('Comparaison avec l’exercice')} {story.year - 1}{p(', à périmètre égal.')}
        </p>
        <div className="pst-cmp">
          <div className="pst-cmp__row">
            <span>{story.year - 1}</span>
            <span className="pst-cmp__track">
              <span className="pst-cmp__bar pst-cmp__bar--past"
                    style={{ width: `${Math.min(100, (story.coutPrev / maxC) * 100)}%` }} />
            </span>
            <span className="pst-cmp__v">{kchf(story.coutPrev)} kCHF</span>
          </div>
          <div className="pst-cmp__row">
            <span>{story.year}</span>
            <span className="pst-cmp__track">
              <span className="pst-cmp__bar pst-cmp__bar--now"
                    style={{ width: `${Math.min(100, (story.coutAnnee / maxC) * 100)}%` }} />
            </span>
            <span className="pst-cmp__v">{kchf(story.coutAnnee)} kCHF</span>
          </div>
        </div>
        <div className="pst-figs pst-figs--tight">
          <Fig value={(story.coutAnnee - story.coutPrev) / 1000} unit="kCHF" sign
               label={p('Variation')} />
          <Fig value={(t.fte || 0) - (story.tw.fte || 0)} dec={1} unit="EPT" sign
               label={p('Effectif')} />
        </div>
      </>) },
    );
  }

  /* ── Chapitre : ce qui compose le coût ───────────────────────────────
     Non pas « rémunérations vs charges » — cette décomposition est un fait
     comptable que personne ne pilote — mais LÀ OÙ VA la masse : par
     département, avec le salaire moyen et le salaire horaire pour la mettre
     à l'échelle. Tout vient du moteur CHR, une seule mesure : la masse
     chargée (rémunérations + charges employeur), la même que la section
     Salaire horaire. */
  const byDept = (() => {
    const m = new Map();
    (now.rows || []).forEach((r) => {
      const d = r.department || p('Sans département');
      m.set(d, (m.get(d) || 0) + (Number(r.loaded_payroll) || 0));
    });
    return [...m.entries()].map(([dept, v]) => ({ dept, v })).sort((a, b) => b.v - a.v);
  })();
  const masseChg = Number(t.loaded_payroll) || 0;
  const eptTot = Number(t.fte) || 0;

  if (byDept.length > 0 && masseChg > 0) {
    const maxD = byDept[0].v;
    chapters.push(
      { key: 'mix', label: p('Ce qui compose le coût'), tone: 'tint', node: (<>
        <h2>
          {byDept[0].dept} {p('porte')} {nb((byDept[0].v / masseChg) * 100, 0)} % {p('de la masse chargée.')}
        </h2>
        <p className="pst-lede">
          {p('Répartition de la masse salariale chargée par département, sur l’exercice.')}
        </p>
        <div className="pst-dept">
          {byDept.map((d) => (
            <div className="pst-dept__row" key={d.dept}>
              <span className="pst-dept__lb">{d.dept}</span>
              <span className="pst-dept__track">
                <span className="pst-dept__bar" style={{ width: `${(d.v / maxD) * 100}%` }} />
              </span>
              <span className="pst-dept__v">{kchf(d.v)}</span>
              <span className="pst-dept__p">{nb((d.v / masseChg) * 100, 0)} %</span>
            </div>
          ))}
        </div>
        <div className="pst-figs pst-figs--tight">
          {/* L'EPT à date met la masse à l'échelle de l'effectif RÉELLEMENT
              porté sur les mois clos — l'EPT annuel, lui, lisse les entrées
              et sorties sur douze mois et dit autre chose. */}
          {story.capacity && story.through && (
            <Fig value={story.capacity.ept_moyen} dec={1} unit="EPT"
                 label={`${p('EPT moyen à date')} · ${MOIS[story.through - 1]}`} />
          )}
          {eptTot > 0 && (
            <Fig value={masseChg / eptTot / 1000} dec={1} unit="kCHF"
                 label={p('Salaire chargé moyen par EPT · année')} />
          )}
          <Fig value={k.lcr_moyen} dec={2} unit="CHF/h" label={p('Salaire horaire (LCR)')} />
        </div>
      </>) },
    );
  }

  if (story.paradoxe) {
    chapters.push(
      { key: 'para', label: p('Ce que dit le taux horaire'), node: (<>
        <h2>
          {story.dLcr < 0
            ? p('L’heure coûte moins cher que l’an dernier.')
            : p('L’heure coûte plus cher que l’an dernier.')}
        </h2>
        <p className="pst-lede">
          {p('Les heures disponibles et la masse salariale n’évoluent pas au même rythme ; le taux horaire suit l’écart. Base : masse chargée, hors frais — comme la section Salaire horaire.')}
        </p>
        <div className="pst-arrows">
          <div className="pst-arw">
            <b>{p('Heures disponibles')}</b>
            <div className={`pst-arw__n ${story.dHeures >= 0 ? 'up' : 'down'}`}>{signed(story.dHeures, pctDec(story.dHeures))} %</div>
            <small>{nb(story.heuresW)} → {nb(story.heures)} h</small>
          </div>
          <div className="pst-arw">
            <b>{p('Salaire horaire moyen')}</b>
            <div className={`pst-arw__n ${story.dLcr >= 0 ? 'up' : 'down'}`}>{signed(story.dLcr, 1)} %</div>
            <small>{nb(story.kw.lcr_moyen, 2)} → {nb(k.lcr_moyen, 2)} CHF/h</small>
          </div>
        </div>
      </>) },
    );
  }

  /* ── Dernier chapitre : ce que coûte une heure ────────────────────────
     Le récit descendait jusqu'au taux SALARIAL et s'arrêtait là. Or le taux
     qui sert à décider, celui qu'on met en face d'un prix, est le coût
     complet : la même heure porte aussi la structure et les frais. Les deux
     se lisent donc côte à côte, chacun avec son évolution.

     Aucune couleur de jugement sur ces variations, à la différence du
     chapitre du paradoxe : un coût horaire qui monte affiché en vert dirait
     « favorable », alors qu'une hausse de taux peut aussi bien venir d'une
     capacité qui s'est réduite. */
  if (ccrNow && ccrNow.ccr != null) {
    const dCcr = (ccrWas && ccrWas.ccr) ? pctVar(ccrNow.ccr, ccrWas.ccr) : null;
    const Rate = ({ label, was, now }) => {
      const d = (was && now) ? pctVar(now, was) : null;
      return (
        <div className="pst-arw">
          <b>{label}</b>
          {d != null ? (
            <>
              <div className="pst-arw__n">{signed(d, pctDec(d))} %</div>
              <small>{nb(was, 2)} → {nb(now, 2)} CHF/h</small>
            </>
          ) : (
            <>
              <div className="pst-arw__n">{nb(now, 2)}</div>
              <small>CHF/h · {story.year}</small>
            </>
          )}
        </div>
      );
    };
    chapters.push(
      { key: 'rates', label: p('Ce que coûte une heure'), tone: 'tint', node: (<>
        <h2>
          {p('Une heure de capacité coûte')} {nb(ccrNow.ccr, 2)} {p('CHF.')}
        </h2>
        <p className="pst-lede">
          {p('Le salaire horaire ne porte que la masse chargée. Le coût horaire y ajoute la structure et les frais, rapportés à la capacité pratique.')}
        </p>
        <div className="pst-arrows">
          <Rate label={p('Salaire horaire (LCR)')}
                was={story.kw ? story.kw.lcr_moyen : null} now={k.lcr_moyen} />
          <Rate label={p('Coût horaire (CCR)')}
                was={ccrWas ? ccrWas.ccr : null} now={ccrNow.ccr} />
        </div>
        {dCcr == null && (
          <p className="pst-foot">
            {p('Pas de comparaison possible avec')} {story.year - 1} {p(': le coût horaire de cet exercice n’est pas calculable.')}
          </p>
        )}
      </>) },
    );
  }

  // Plusieurs chapitres sont CONDITIONNELS : la numérotation suit donc les
  // chapitres réellement construits, pas une liste écrite d'avance.
  return <StoryDeck numbered chapters={chapters} print={print} />;
}
